import { useUniwind } from "@-/com.npmjs.uniwind";

export type ResolvedColorScheme = "light" | "dark";

/**
 * The app's effective light/dark scheme — the single source of truth for any code that branches
 * on theme to derive colors (tone-color palettes, glyph stroke fills, etc.).
 *
 * Use this instead of React Native's raw `useColorScheme()`. The app drives its theme through the
 * device `theme` setting → `Uniwind.setTheme(...)` (see `use-nav-context-value.ts`), which both
 * sets the native `Appearance` color scheme AND normalizes `unspecified`/`null` to `"light"`. Raw
 * `useColorScheme()` skips that normalization (returning `null` — or, on iOS, a stale/forced value
 * — for the `unspecified` appearance the `system` theme installs), so it can disagree with the
 * theme the rest of the UI (anything styled with a Uniwind `className`) actually resolved against.
 * That divergence rendered dark-mode tone colors and near-white glyph strokes while the app was in
 * light mode. `useUniwind().theme` is exactly the value the className cascade uses, so reading it
 * here keeps these JS-resolved colors locked to the same scheme as everything else.
 */
export function useTheme(): ResolvedColorScheme {
  const { theme } = useUniwind();
  return theme === "dark" ? "dark" : "light";
}
